import React, { useEffect, useRef, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import Seo from "../components/Seo";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { ProductCard } from "../components/ProductCard";
import { api } from "../lib/api";
import { useContent } from "../context/ContentContext";

const SORTS = [
  { value: "featured", label: "Featured" },
  { value: "newest", label: "Newest" },
  { value: "title", label: "Title A–Z" },
  { value: "price", label: "Price" },
];
const LIMIT = 12;

export default function Etchings() {
  const { p } = useContent();
  const hdr = p("etchings").header;
  const ui = p("etchings").ui;
  const [products, setProducts] = useState([]);
  const [cats, setCats] = useState({ categories: [], available: 0, total: 0 });
  const [category, setCategory] = useState("all");
  const [onlyAvail, setOnlyAvail] = useState(false);
  const [sort, setSort] = useState("featured");
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const sentinel = useRef(null);
  const loadingRef = useRef(false);
  const filterKey = `${category}|${onlyAvail}|${sort}`;

  useEffect(() => { api.get("/products/categories").then((r) => setCats(r.data)).catch(() => {}); }, []);

  const fetchPage = useCallback(async (reset, force) => {
    if (loadingRef.current && !force) return;
    loadingRef.current = true; setLoading(true);
    const off = reset ? 0 : offset;
    const params = { kind: "etching", sort, limit: LIMIT, offset: off };
    if (category !== "all") params.category = category;
    if (onlyAvail) params.available = true;
    try {
      const r = await api.get("/products/browse", { params });
      setProducts((prev) => (reset ? r.data.items : [...prev, ...r.data.items]));
      setOffset(off + r.data.items.length);
      setHasMore(r.data.has_more);
    } catch { setHasMore(false); } finally { setLoading(false); loadingRef.current = false; }
  }, [offset, category, onlyAvail, sort]);

  useEffect(() => {
    setProducts([]); setOffset(0); setHasMore(true);
    fetchPage(true, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterKey]);

  useEffect(() => {
    const el = sentinel.current;
    if (!el) return;
    const io = new IntersectionObserver((e) => {
      if (e[0].isIntersecting && hasMore && !loadingRef.current) fetchPage(false);
    }, { rootMargin: "500px" });
    io.observe(el);
    return () => io.disconnect();
  }, [hasMore, fetchPage]);

  const chips = [{ slug: "all", label: "All" }, ...cats.categories];

  return (
    <div className="max-w-7xl mx-auto px-5 sm:px-8 py-14" data-testid="etchings-page">
      <Seo title="Original Etchings — CaminoSapiens" description="A curated collection of original hand-pulled photogravure etchings inspired by the Camino de Santiago and long-distance walking." image="https://images.pexels.com/photos/34159379/pexels-photo-34159379.jpeg?auto=compress&cs=tinysrgb&w=1200" />
      <Breadcrumbs items={[{ label: "Home", to: "/" }, { label: "Original Etchings" }]} />
      <div className="max-w-2xl mb-12">
        <p className="overline mb-4">{hdr.eyebrow}</p>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-tight">{hdr.heading}</h1>
        <p className="text-[#4A4947] mt-6 leading-relaxed">
          {hdr.body}
        </p>
      </div>

      {/* Restrained filter / sort bar */}
      <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-4 mb-12 border-b border-[#E4E1DB] pb-5" data-testid="etchings-toolbar">
        <div className="flex flex-wrap items-center gap-x-5 gap-y-2 min-h-[77px] sm:min-h-0" data-testid="etchings-chips">
          {chips.map((c) => (
            <button key={c.slug} onClick={() => setCategory(c.slug)} data-testid={`etch-cat-${c.slug}`}
              className={`text-sm transition-colors ${category === c.slug ? "text-[#1C1B1A] border-b border-[#B35D4B]" : "text-[#9a9793] hover:text-[#4A4947]"}`}>
              {c.label}
            </button>
          ))}
          <button onClick={() => setOnlyAvail((v) => !v)} data-testid="etch-available"
            className={`text-sm transition-colors ${onlyAvail ? "text-[#1C1B1A] border-b border-[#B35D4B]" : "text-[#9a9793] hover:text-[#4A4947]"}`}>
            {ui.available_label}{cats.available ? ` (${cats.available})` : ""}
          </button>
        </div>
        <label className="flex items-center gap-2 text-sm text-[#4A4947]">
          <span className="overline">{ui.sort_label}</span>
          <select value={sort} onChange={(e) => setSort(e.target.value)} data-testid="etch-sort"
            className="bg-transparent border-b border-[#E4E1DB] py-1 pr-6 text-sm focus:outline-none focus:border-[#B35D4B] cursor-pointer">
            {SORTS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
        </label>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-14">
        {products.map((p, i) => <ProductCard key={p.id} p={p} index={i} />)}
      </div>
      <div ref={sentinel} className="h-10" />
      {loading && <p className="text-center text-sm text-[#9a9793] py-6">Loading…</p>}
      {!loading && products.length === 0 && <p className="text-[#4A4947]">{ui.empty_text}</p>}
      {hasMore && !loading && (
        <div className="text-center mt-4">
          <button onClick={() => fetchPage(false)} data-testid="etch-load-more" className="border border-[#1C1B1A] px-6 py-2.5 text-sm hover:bg-[#1C1B1A] hover:text-white transition-colors">{ui.load_more_label}</button>
        </div>
      )}

      <div className="mt-20 pt-8 border-t border-[#E4E1DB] text-center">
        <Link to="/etchings/archive" data-testid="etch-archive-link" className="text-sm text-[#9a9793] hover:text-[#B35D4B] transition-colors link-underline">
          {ui.archive_link_label || "View the archive of sold works →"}
        </Link>
      </div>
    </div>
  );
}
